<template>
  <div>
    <v-container fluid>
      <!-- Header Card -->
      <base-material-card
        icon="mdi-clipboard-flow"
        :title="pageTitle"
        class="px-5 py-3"
      >
        <!-- Alert Instructions -->
        <v-alert text color="error" class="mt-2">
          <h3 class="text-h6 mb-2">คำแนะนำการใช้งาน</h3>
          <p class="body-1">
            กรอกข้อมูล <strong>รหัสบัตรประชาชน</strong> และ
            <strong>ปี พ.ศ. เกิด</strong> เพื่อค้นหาข้อมูลของท่าน
          </p>
          <v-divider class="my-3"></v-divider>
          <div class="d-flex align-center">
            <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
            <span class="body-1">
              กรุณาดำเนินการก่อนวันรายงานตัวเลือกสถานศึกษา
            </span>
          </div>
        </v-alert>

        <!-- Search Section -->
        <v-card outlined class="mb-4">
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="searchForm.idCard"
                  label="รหัสบัตรประชาชน"
                  prepend-inner-icon="mdi-card-account-details"
                  outlined
                  dense
                  :rules="idCardRules"
                  maxlength="13"
                  counter
                  @input="formatIdCard"
                  hint="กรอกเลข 13 หลัก"
                />
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="searchForm.birthYear"
                  label="ปี พ.ศ. เกิด (เช่น 2543)"
                  prepend-inner-icon="mdi-calendar"
                  outlined
                  dense
                  type="number"
                  :rules="birthYearRules"
                  maxlength="4"
                  hint="ระบุปี พ.ศ. 4 หลัก"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-btn
                  color="primary"
                  large
                  block
                  @click="handleSearch"
                  :loading="loading"
                  :disabled="!isSearchValid"
                  elevation="2"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  ค้นหา
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Profile Form -->
        <v-form ref="profileForm" v-model="formValid" lazy-validation>
          <div v-if="teacherData.mt_id_card">
            <!-- Status Approved -->
            <template v-if="isApproved">
              <v-card outlined class="approved-card">
                <v-card-title class="success white--text justify-center">
                  <v-icon color="white" class="mr-2" size="32"
                    >mdi-check-circle</v-icon
                  >
                  <span class="text-h5">ผ่านการตรวจสอบแล้ว</span>
                </v-card-title>

                <v-card-text class="pt-6">
                  <!-- Profile Photo Display -->
                  <div class="text-center mb-6">
                    <v-avatar size="200" class="profile-avatar">
                      <v-img
                        v-if="teacherData.mt_picprofile"
                        :src="
                          `https://hrvec.ovec.go.th/HRvecfiles/${teacherData.mt_picprofile}`
                        "
                      />
                      <v-img v-else src="https://hrvec.ovec.go.th/empty.png" />
                    </v-avatar>
                  </div>

                  <v-row>
                    <!-- Personal Info -->
                    <v-col cols="12" md="6">
                      <v-list two-line>
                        <v-list-item>
                          <v-list-item-icon>
                            <v-icon color="primary">mdi-account</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="text-h6">
                              {{ fullName }}
                            </v-list-item-title>
                            <v-list-item-subtitle>
                              {{ teacherData.mt_id_card }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item>
                          <v-list-item-icon>
                            <v-icon color="primary">mdi-cake-variant</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>{{
                              birthDate
                            }}</v-list-item-title>
                            <v-list-item-subtitle
                              >วัน-เดือน-ปีเกิด</v-list-item-subtitle
                            >
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item>
                          <v-list-item-icon>
                            <v-icon color="primary">mdi-phone</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>{{
                              teacherData.mt_tel_p
                            }}</v-list-item-title>
                            <v-list-item-subtitle
                              >เบอร์โทรติดต่อ</v-list-item-subtitle
                            >
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-col>

                    <!-- Education Info -->
                    <v-col cols="12" md="6">
                      <v-list two-line>
                        <v-list-item>
                          <v-list-item-icon>
                            <v-icon color="primary">mdi-school</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>
                              {{ teacherData.mt_id_branch }} :
                              {{ teacherData.name_branch }}
                            </v-list-item-title>
                            <v-list-item-subtitle
                              >สาขาวิชา</v-list-item-subtitle
                            >
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item>
                          <v-list-item-icon>
                            <v-icon color="primary">mdi-certificate</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>
                              {{ teacherData.mt_ed_abb }}
                              {{ teacherData.mt_ed_name }}
                            </v-list-item-title>
                            <v-list-item-subtitle
                              >วุฒิการศึกษา</v-list-item-subtitle
                            >
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-col>
                  </v-row>

                  <v-divider class="my-4"></v-divider>

                  <!-- License Info -->
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="text-center pa-4 license-section">
                        <h3 class="mb-3">
                          <v-icon
                            :color="hasLicense ? 'success' : 'warning'"
                            class="mr-2"
                          >
                            {{
                              hasLicense
                                ? "mdi-check-circle"
                                : "mdi-alert-circle"
                            }}
                          </v-icon>
                          สถานะใบอนุญาตประกอบวิชาชีพครู
                        </h3>
                        <v-chip
                          :color="hasLicense ? 'success' : 'warning'"
                          text-color="white"
                          large
                        >
                          {{
                            hasLicense
                              ? "มีใบประกอบวิชาชีพครู"
                              : "ไม่มีใบประกอบวิชาชีพ"
                          }}
                        </v-chip>

                        <template v-if="hasLicense">
                          <div class="mt-4">
                            <p>
                              <strong>เลขที่:</strong>
                              {{ teacherData.pt_licence }}
                            </p>
                            <p>
                              <strong>วันที่ออกบัตร:</strong>
                              {{ formatThaiDate(teacherData.pt_ondate) }}
                            </p>
                            <p>
                              <strong>วันหมดอายุ:</strong>
                              {{ formatThaiDate(teacherData.pt_enddate) }}
                            </p>
                          </div>
                          <v-btn
                            v-if="teacherData.pt_licence_file"
                            outlined
                            color="primary"
                            class="mt-3"
                            @click="viewDocument('license')"
                          >
                            <v-icon left>mdi-file-pdf-box</v-icon>
                            ดูใบอนุญาต
                          </v-btn>
                        </template>
                      </div>
                    </v-col>

                    <!-- Documents -->
                    <v-col cols="12" md="6">
                      <div class="text-center pa-4">
                        <h3 class="mb-3">เอกสารประกอบ</h3>
                        <v-list dense>
                          <v-list-item
                            v-if="teacherData.mt_ed_file"
                            @click="viewDocument('education')"
                            class="document-item"
                          >
                            <v-list-item-icon>
                              <v-icon color="primary">mdi-file-pdf-box</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title
                                >สำเนาปริญญาบัตร</v-list-item-title
                              >
                            </v-list-item-content>
                            <v-list-item-action>
                              <v-icon>mdi-chevron-right</v-icon>
                            </v-list-item-action>
                          </v-list-item>

                          <v-list-item
                            v-if="teacherData.mt_transcript"
                            @click="viewDocument('transcript')"
                            class="document-item"
                          >
                            <v-list-item-icon>
                              <v-icon color="primary">mdi-file-pdf-box</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title
                                >ใบรายงานผลการเรียน</v-list-item-title
                              >
                            </v-list-item-content>
                            <v-list-item-action>
                              <v-icon>mdi-chevron-right</v-icon>
                            </v-list-item-action>
                          </v-list-item>

                          <v-list-item
                            v-if="teacherData.mt_idcard_copy"
                            @click="viewDocument('idCard')"
                            class="document-item"
                          >
                            <v-list-item-icon>
                              <v-icon color="primary">mdi-file-pdf-box</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title
                                >สำเนาบัตรประชาชน</v-list-item-title
                              >
                            </v-list-item-content>
                            <v-list-item-action>
                              <v-icon>mdi-chevron-right</v-icon>
                            </v-list-item-action>
                          </v-list-item>

                          <v-list-item
                            v-if="teacherData.mt_change_name"
                            @click="viewDocument('nameChange')"
                            class="document-item"
                          >
                            <v-list-item-icon>
                              <v-icon color="primary">mdi-file-pdf-box</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title
                                >เอกสารเปลี่ยนชื่อ-สกุล</v-list-item-title
                              >
                            </v-list-item-content>
                            <v-list-item-action>
                              <v-icon>mdi-chevron-right</v-icon>
                            </v-list-item-action>
                          </v-list-item>
                        </v-list>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </template>

            <!-- Status Pending/Rejected -->
            <template v-else>
              <v-card outlined class="edit-card">
                <!-- Rejection Message -->
                <v-alert
                  v-if="isRejected"
                  type="error"
                  prominent
                  border="left"
                  class="ma-4"
                >
                  <v-row align="center">
                    <v-col class="grow">
                      <div class="text-h6">
                        {{ teacherData.mt_status_comment }}
                      </div>
                      <div class="body-2 mt-2">
                        กรุณาตรวจสอบและแก้ไขข้อมูลอีกครั้ง
                      </div>
                    </v-col>
                  </v-row>
                </v-alert>

                <v-card-text class="pt-4">
                  <!-- Profile Photo Upload -->
                  <div class="text-center mb-6">
                    <v-avatar size="200" class="profile-avatar mb-4">
                      <v-img
                        v-if="teacherData.mt_picprofile"
                        :src="
                          `https://hrvec.ovec.go.th/HRvecfiles/${teacherData.mt_picprofile}`
                        "
                      />
                      <v-img v-else src="https://hrvec.ovec.go.th/empty.png" />
                    </v-avatar>

                    <div
                      v-if="!teacherData.mt_picprofile"
                      class="upload-section"
                    >
                      <v-file-input
                        v-model="uploadFiles.photo"
                        accept="image/jpeg,image/jpg"
                        label="อัพโหลดรูปถ่าย 1 นิ้ว (JPG)"
                        prepend-icon="mdi-camera"
                        outlined
                        dense
                        :rules="[...fileRules, ...imageRules]"
                        show-size
                        counter
                        class="mx-auto"
                        style="max-width: 400px"
                      >
                        <template v-slot:selection="{ text }">
                          <v-chip small color="primary">{{ text }}</v-chip>
                        </template>
                      </v-file-input>
                      <v-btn
                        color="success"
                        @click="handlePhotoUpload"
                        :disabled="!uploadFiles.photo"
                        :loading="uploading.photo"
                      >
                        <v-icon left>mdi-upload</v-icon>
                        อัพโหลด
                      </v-btn>
                    </div>
                    <div v-else>
                      <v-btn color="error" outlined @click="handlePhotoDelete">
                        <v-icon left>mdi-delete</v-icon>
                        ลบรูปภาพ
                      </v-btn>
                    </div>
                  </div>

                  <!-- Personal Information -->
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="pa-4">
                        <h3 class="mb-4">
                          <v-icon color="primary" class="mr-2"
                            >mdi-account-details</v-icon
                          >
                          ข้อมูลส่วนตัว
                        </h3>

                        <div class="info-item">
                          <strong>ชื่อ-นามสกุล:</strong>
                          <p>{{ fullName }}</p>
                        </div>

                        <div class="info-item">
                          <strong>รหัสบัตรประชาชน:</strong>
                          <p>{{ teacherData.mt_id_card }}</p>
                        </div>

                        <div class="info-item">
                          <strong>วัน-เดือน-ปีเกิด:</strong>
                          <p>{{ birthDate }}</p>
                        </div>

                        <v-text-field
                          v-model="teacherData.mt_tel_p"
                          label="เบอร์โทรศัพท์"
                          prepend-icon="mdi-phone"
                          outlined
                          dense
                          :rules="phoneRules"
                          maxlength="10"
                          counter
                          hint="ตัวอย่าง: 0812345678"
                          @blur="updateProfile"
                        />
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-card outlined class="pa-4">
                        <h3 class="mb-4">
                          <v-icon color="primary" class="mr-2"
                            >mdi-school</v-icon
                          >
                          ข้อมูลการศึกษา
                        </h3>

                        <div class="info-item">
                          <strong>สาขาวิชา:</strong>
                          <p>
                            {{ teacherData.mt_id_branch }} :
                            {{ teacherData.name_branch }}
                          </p>
                        </div>

                        <div class="info-item">
                          <strong>วุฒิการศึกษา:</strong>
                          <p>
                            {{ teacherData.mt_ed_abb }}
                            {{ teacherData.mt_ed_name }}
                          </p>
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>

                  <v-divider class="my-6"></v-divider>

                  <!-- License Status Selection -->
                  <v-card outlined class="pa-4 mb-4">
                    <h3 class="mb-4">
                      <v-icon color="primary" class="mr-2"
                        >mdi-card-account-details</v-icon
                      >
                      สถานะใบอนุญาตประกอบวิชาชีพครู
                    </h3>

                    <v-radio-group v-model="teacherData.mt_license_status" row>
                      <v-radio value="have" color="success">
                        <template v-slot:label>
                          <div>
                            <v-icon color="success" class="mr-1"
                              >mdi-check-circle</v-icon
                            >
                            <strong class="success--text"
                              >มีใบประกอบวิชาชีพครู</strong
                            >
                          </div>
                        </template>
                      </v-radio>
                      <v-radio value="donot" color="warning">
                        <template v-slot:label>
                          <div>
                            <v-icon color="warning" class="mr-1"
                              >mdi-alert-circle</v-icon
                            >
                            <strong class="warning--text"
                              >ไม่มีใบประกอบวิชาชีพ</strong
                            >
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                  </v-card>

                  <!-- License Information (if have license) -->
                  <template v-if="hasLicense">
                    <v-card outlined class="pa-4 mb-4">
                      <h3 class="mb-4">ข้อมูลใบอนุญาตประกอบวิชาชีพครู</h3>

                      <!-- License File Upload -->
                      <div v-if="!teacherData.pt_licence_file" class="mb-4">
                        <v-file-input
                          v-model="uploadFiles.license"
                          accept=".pdf"
                          label="อัพโหลดใบอนุญาตประกอบวิชาชีพครู (PDF)"
                          prepend-icon="mdi-file-pdf-box"
                          outlined
                          dense
                          :rules="[...fileRules, ...pdfRules]"
                          show-size
                          counter
                        />
                        <v-btn
                          color="success"
                          @click="handleLicenseFileUpload"
                          :disabled="!uploadFiles.license"
                          :loading="uploading.license"
                        >
                          <v-icon left>mdi-upload</v-icon>
                          อัพโหลด
                        </v-btn>
                      </div>
                      <div v-else class="mb-4">
                        <v-btn
                          color="primary"
                          outlined
                          @click="viewDocument('license')"
                          class="mr-2"
                        >
                          <v-icon left>mdi-file-pdf-box</v-icon>
                          ดูไฟล์
                        </v-btn>
                        <v-btn
                          color="error"
                          outlined
                          @click="handleLicenseFileDelete"
                        >
                          <v-icon left>mdi-delete</v-icon>
                          ลบไฟล์
                        </v-btn>
                      </div>

                      <!-- License Details -->
                      <v-row>
                        <v-col cols="12">
                          <v-text-field
                            v-model="teacherData.pt_licence"
                            label="เลขที่ใบอนุญาต"
                            prepend-icon="mdi-card-account-details"
                            outlined
                            dense
                            :rules="licenseNumberRules"
                            @blur="updateProfile"
                          />
                        </v-col>

                        <v-col cols="12" md="6">
                          <v-menu
                            v-model="datePickers.onDate"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                :value="formatThaiDate(teacherData.pt_ondate)"
                                label="วันที่ออกบัตร"
                                prepend-icon="mdi-calendar"
                                readonly
                                outlined
                                dense
                                v-bind="attrs"
                                v-on="on"
                                :rules="dateRules"
                              />
                            </template>
                            <v-date-picker
                              v-model="teacherData.pt_ondate"
                              @input="
                                datePickers.onDate = false;
                                updateProfile();
                              "
                              locale="th"
                              no-title
                              scrollable
                            />
                          </v-menu>
                        </v-col>

                        <v-col cols="12" md="6">
                          <v-menu
                            v-model="datePickers.endDate"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                :value="formatThaiDate(teacherData.pt_enddate)"
                                label="วันที่หมดอายุ"
                                prepend-icon="mdi-calendar"
                                readonly
                                outlined
                                dense
                                v-bind="attrs"
                                v-on="on"
                                :rules="dateRules"
                              />
                            </template>
                            <v-date-picker
                              v-model="teacherData.pt_enddate"
                              @input="
                                datePickers.endDate = false;
                                updateProfile();
                              "
                              locale="th"
                              no-title
                              scrollable
                            />
                          </v-menu>
                        </v-col>
                      </v-row>
                    </v-card>
                  </template>

                  <!-- Document Upload (if no license) -->
                  <template v-if="!hasLicense">
                    <v-card outlined class="pa-4">
                      <h3 class="mb-4">
                        <v-icon color="primary" class="mr-2"
                          >mdi-file-document</v-icon
                        >
                        อัพโหลดเอกสารประกอบ
                      </h3>

                      <!-- Education Document -->
                      <div class="document-upload-section mb-4">
                        <h4 class="mb-2">
                          1. สำเนาปริญญาบัตร หรือหนังสือรับรองคุณวุฒิ
                          <v-chip
                            x-small
                            color="error"
                            text-color="white"
                            class="ml-2"
                            >จำเป็น</v-chip
                          >
                        </h4>
                        <div v-if="!teacherData.mt_ed_file">
                          <v-file-input
                            v-model="uploadFiles.education"
                            accept=".pdf"
                            label="เลือกไฟล์ PDF"
                            prepend-icon="mdi-file-pdf-box"
                            outlined
                            dense
                            :rules="[...fileRules, ...pdfRules]"
                            show-size
                          />
                          <v-btn
                            color="success"
                            @click="handleDocumentUpload('education')"
                            :disabled="!uploadFiles.education"
                            :loading="uploading.education"
                          >
                            <v-icon left>mdi-upload</v-icon>
                            อัพโหลด
                          </v-btn>
                        </div>
                        <div v-else class="d-flex align-center">
                          <v-chip color="success" class="mr-2">
                            <v-icon left small>mdi-check</v-icon>
                            อัพโหลดแล้ว
                          </v-chip>
                          <v-btn
                            small
                            color="primary"
                            outlined
                            @click="viewDocument('education')"
                            class="mr-2"
                          >
                            <v-icon left small>mdi-eye</v-icon>
                            ดู
                          </v-btn>
                          <v-btn
                            small
                            color="error"
                            outlined
                            @click="handleDocumentDelete('education')"
                          >
                            <v-icon left small>mdi-delete</v-icon>
                            ลบ
                          </v-btn>
                        </div>
                      </div>

                      <v-divider class="my-3"></v-divider>

                      <!-- Transcript Document -->
                      <div class="document-upload-section mb-4">
                        <h4 class="mb-2">
                          2. สำเนาใบรายงานผลการเรียน (Transcript)
                          <v-chip
                            x-small
                            color="error"
                            text-color="white"
                            class="ml-2"
                            >จำเป็น</v-chip
                          >
                        </h4>
                        <div v-if="!teacherData.mt_transcript">
                          <v-file-input
                            v-model="uploadFiles.transcript"
                            accept=".pdf"
                            label="เลือกไฟล์ PDF"
                            prepend-icon="mdi-file-pdf-box"
                            outlined
                            dense
                            :rules="[...fileRules, ...pdfRules]"
                            show-size
                          />
                          <v-btn
                            color="success"
                            @click="handleDocumentUpload('transcript')"
                            :disabled="!uploadFiles.transcript"
                            :loading="uploading.transcript"
                          >
                            <v-icon left>mdi-upload</v-icon>
                            อัพโหลด
                          </v-btn>
                        </div>
                        <div v-else class="d-flex align-center">
                          <v-chip color="success" class="mr-2">
                            <v-icon left small>mdi-check</v-icon>
                            อัพโหลดแล้ว
                          </v-chip>
                          <v-btn
                            small
                            color="primary"
                            outlined
                            @click="viewDocument('transcript')"
                            class="mr-2"
                          >
                            <v-icon left small>mdi-eye</v-icon>
                            ดู
                          </v-btn>
                          <v-btn
                            small
                            color="error"
                            outlined
                            @click="handleDocumentDelete('transcript')"
                          >
                            <v-icon left small>mdi-delete</v-icon>
                            ลบ
                          </v-btn>
                        </div>
                      </div>

                      <v-divider class="my-3"></v-divider>

                      <!-- ID Card Document -->
                      <div class="document-upload-section mb-4">
                        <h4 class="mb-2">
                          3. สำเนาบัตรประจำตัวประชาชน
                          <v-chip
                            x-small
                            color="error"
                            text-color="white"
                            class="ml-2"
                            >จำเป็น</v-chip
                          >
                        </h4>
                        <div v-if="!teacherData.mt_idcard_copy">
                          <v-file-input
                            v-model="uploadFiles.idCard"
                            accept=".pdf"
                            label="เลือกไฟล์ PDF"
                            prepend-icon="mdi-file-pdf-box"
                            outlined
                            dense
                            :rules="[...fileRules, ...pdfRules]"
                            show-size
                          />
                          <v-btn
                            color="success"
                            @click="handleDocumentUpload('idCard')"
                            :disabled="!uploadFiles.idCard"
                            :loading="uploading.idCard"
                          >
                            <v-icon left>mdi-upload</v-icon>
                            อัพโหลด
                          </v-btn>
                        </div>
                        <div v-else class="d-flex align-center">
                          <v-chip color="success" class="mr-2">
                            <v-icon left small>mdi-check</v-icon>
                            อัพโหลดแล้ว
                          </v-chip>
                          <v-btn
                            small
                            color="primary"
                            outlined
                            @click="viewDocument('idCard')"
                            class="mr-2"
                          >
                            <v-icon left small>mdi-eye</v-icon>
                            ดู
                          </v-btn>
                          <v-btn
                            small
                            color="error"
                            outlined
                            @click="handleDocumentDelete('idCard')"
                          >
                            <v-icon left small>mdi-delete</v-icon>
                            ลบ
                          </v-btn>
                        </div>
                      </div>

                      <v-divider class="my-3"></v-divider>

                      <!-- Name Change Document (Optional) -->
                      <div class="document-upload-section">
                        <h4 class="mb-2">
                          4. เอกสารการเปลี่ยนชื่อ-สกุล หรือทะเบียนสมรส/หย่า
                          <v-chip
                            x-small
                            color="info"
                            text-color="white"
                            class="ml-2"
                            >ถ้ามี</v-chip
                          >
                        </h4>
                        <div v-if="!teacherData.mt_change_name">
                          <v-file-input
                            v-model="uploadFiles.nameChange"
                            accept=".pdf"
                            label="เลือกไฟล์ PDF"
                            prepend-icon="mdi-file-pdf-box"
                            outlined
                            dense
                            :rules="fileRules"
                            show-size
                          />
                          <v-btn
                            color="success"
                            @click="handleDocumentUpload('nameChange')"
                            :disabled="!uploadFiles.nameChange"
                            :loading="uploading.nameChange"
                          >
                            <v-icon left>mdi-upload</v-icon>
                            อัพโหลด
                          </v-btn>
                        </div>
                        <div v-else class="d-flex align-center">
                          <v-chip color="success" class="mr-2">
                            <v-icon left small>mdi-check</v-icon>
                            อัพโหลดแล้ว
                          </v-chip>
                          <v-btn
                            small
                            color="primary"
                            outlined
                            @click="viewDocument('nameChange')"
                            class="mr-2"
                          >
                            <v-icon left small>mdi-eye</v-icon>
                            ดู
                          </v-btn>
                          <v-btn
                            small
                            color="error"
                            outlined
                            @click="handleDocumentDelete('nameChange')"
                          >
                            <v-icon left small>mdi-delete</v-icon>
                            ลบ
                          </v-btn>
                        </div>
                      </div>
                    </v-card>
                  </template>

                  <!-- Confirmation Requirements -->
                  <v-alert
                    v-if="!canConfirm"
                    type="warning"
                    outlined
                    prominent
                    class="mt-4"
                  >
                    <h3 class="mb-2">กรุณาดำเนินการให้ครบถ้วนก่อนยืนยัน</h3>
                    <ul class="pl-4">
                      <li v-if="!teacherData.mt_picprofile">อัพโหลดรูปถ่าย</li>
                      <li v-if="!teacherData.mt_tel_p">กรอกเบอร์โทรศัพท์</li>
                      <template v-if="hasLicense">
                        <li v-if="!teacherData.pt_licence_file">
                          อัพโหลดใบอนุญาตประกอบวิชาชีพครู
                        </li>
                        <li v-if="!teacherData.pt_licence">
                          กรอกเลขที่ใบอนุญาต
                        </li>
                        <li v-if="!teacherData.pt_ondate">
                          เลือกวันที่ออกบัตร
                        </li>
                        <li v-if="!teacherData.pt_enddate">
                          เลือกวันที่หมดอายุ
                        </li>
                      </template>
                      <template v-else>
                        <li v-if="!teacherData.mt_ed_file">
                          อัพโหลดสำเนาปริญญาบัตร
                        </li>
                        <li v-if="!teacherData.mt_transcript">
                          อัพโหลดใบรายงานผลการเรียน
                        </li>
                        <li v-if="!teacherData.mt_idcard_copy">
                          อัพโหลดสำเนาบัตรประชาชน
                        </li>
                      </template>
                    </ul>
                  </v-alert>

                  <!-- Action Buttons -->
                  <v-card-actions class="justify-center mt-6">
                    <v-btn
                      color="success"
                      x-large
                      @click="handleConfirm"
                      :disabled="!canConfirm"
                      :loading="submitting"
                      elevation="4"
                    >
                      <v-icon left size="28">mdi-check-circle</v-icon>
                      ยืนยันรายการ (บันทึกข้อมูล)
                    </v-btn>
                  </v-card-actions>
                </v-card-text>
              </v-card>
            </template>
          </div>
        </v-form>
      </base-material-card>

      <!-- PDF Viewer Dialogs -->
      <v-dialog v-model="pdfDialogs.license" max-width="90%" max-height="90%">
        <v-card>
          <v-card-title class="primary white--text">
            ใบอนุญาตประกอบวิชาชีพครู
            <v-spacer></v-spacer>
            <v-btn icon dark @click="pdfDialogs.license = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <embed
              :src="`/HRvecLicenseTeach/${teacherData.pt_licence_file}`"
              width="100%"
              height="700px"
              type="application/pdf"
            />
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="pdfDialogs.education" max-width="90%" max-height="90%">
        <v-card>
          <v-card-title class="primary white--text">
            สำเนาปริญญาบัตร
            <v-spacer></v-spacer>
            <v-btn icon dark @click="pdfDialogs.education = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <embed
              :src="`/HREducation/${teacherData.mt_ed_file}`"
              width="100%"
              height="700px"
              type="application/pdf"
            />
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="pdfDialogs.transcript"
        max-width="90%"
        max-height="90%"
      >
        <v-card>
          <v-card-title class="primary white--text">
            ใบรายงานผลการเรียน (Transcript)
            <v-spacer></v-spacer>
            <v-btn icon dark @click="pdfDialogs.transcript = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <embed
              :src="`/HREducation/${teacherData.mt_transcript}`"
              width="100%"
              height="700px"
              type="application/pdf"
            />
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="pdfDialogs.idCard" max-width="90%" max-height="90%">
        <v-card>
          <v-card-title class="primary white--text">
            สำเนาบัตรประชาชน
            <v-spacer></v-spacer>
            <v-btn icon dark @click="pdfDialogs.idCard = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <embed
              :src="`/HRIDcard/${teacherData.mt_idcard_copy}`"
              width="100%"
              height="700px"
              type="application/pdf"
            />
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="pdfDialogs.nameChange"
        max-width="90%"
        max-height="90%"
      >
        <v-card>
          <v-card-title class="primary white--text">
            เอกสารการเปลี่ยนชื่อ-สกุล
            <v-spacer></v-spacer>
            <v-btn icon dark @click="pdfDialogs.nameChange = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <embed
              :src="`/HRIDcard/${teacherData.mt_change_name}`"
              width="100%"
              height="700px"
              type="application/pdf"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "TeacherRegistration",

  data() {
    return {
      // API & Config
      apiKey: "HRvec2021",
      loading: false,
      formValid: false,
      submitting: false,

      // Search Form
      searchForm: {
        idCard: "",
        birthYear: ""
      },

      // Period Info
      periodInfo: {},

      // Teacher Data
      teacherData: {
        mt_id_card: "",
        mt_title_s: "",
        mt_frist_name: "",
        mt_last_name: "",
        mt_brith_day: "",
        mt_brith_month: "",
        mt_brith_year: "",
        mt_tel_p: "",
        mt_picprofile: "",
        mt_status_report: "",
        mt_status_comment: "",
        mt_license_status: "donot",
        pt_licence_file: "",
        pt_licence: "",
        pt_ondate: "",
        pt_enddate: "",
        mt_ed_file: "",
        mt_transcript: "",
        mt_idcard_copy: "",
        mt_change_name: "",
        mt_id_branch: "",
        name_branch: "",
        mt_ed_abb: "",
        mt_ed_name: ""
      },

      // File Upload
      uploadFiles: {
        photo: null,
        license: null,
        education: null,
        transcript: null,
        idCard: null,
        nameChange: null
      },

      // Upload Status
      uploading: {
        photo: false,
        license: false,
        education: false,
        transcript: false,
        idCard: false,
        nameChange: false
      },

      // PDF Dialogs
      pdfDialogs: {
        license: false,
        education: false,
        transcript: false,
        idCard: false,
        nameChange: false
      },

      // Date Pickers
      datePickers: {
        onDate: false,
        endDate: false
      },

      // Validation Rules
      idCardRules: [
        v => !!v || "กรุณากรอกรหัสบัตรประชาชน",
        v => /^\d{13}$/.test(v) || "รหัสบัตรประชาชนต้องเป็นตัวเลข 13 หลัก"
      ],

      birthYearRules: [
        v => !!v || "กรุณากรอกปีเกิด",
        v => /^\d{4}$/.test(v) || "ปีเกิดต้องเป็นตัวเลข 4 หลัก",
        v => {
          const year = parseInt(v);
          return (
            (year >= 2500 && year <= 2570) ||
            "ปีเกิดต้องอยู่ระหว่าง พ.ศ. 2500-2570"
          );
        }
      ],

      fileRules: [v => !v || v.size < 5242880 || "ไฟล์ต้องมีขนาดไม่เกิน 5 MB"],

      imageRules: [
        v =>
          !v ||
          /\.(jpg|jpeg)$/i.test(v.name) ||
          "ต้องเป็นไฟล์ JPG หรือ JPEG เท่านั้น"
      ],

      pdfRules: [
        v => !v || /\.pdf$/i.test(v.name) || "ต้องเป็นไฟล์ PDF เท่านั้น"
      ],

      phoneRules: [
        v => !!v || "กรุณากรอกเบอร์โทรศัพท์",
        v =>
          /^0\d{9}$/.test(v) || "เบอร์โทรศัพท์ไม่ถูกต้อง (ตัวอย่าง: 0812345678)"
      ],

      licenseNumberRules: [
        v => !!v || "กรุณากรอกเลขที่ใบอนุญาต",
        v => (v && v.length >= 5) || "เลขที่ใบอนุญาตต้องมีอย่างน้อย 5 หลัก"
      ],

      dateRules: [v => !!v || "กรุณาเลือกวันที่"]
    };
  },

  computed: {
    pageTitle() {
      return `รายงานตัว ก่อนเลือกสถานศึกษา ตำแหน่งครูผู้ช่วย รอบที่ : ${this
        .periodInfo.periodAssTeachTimes || "-"} / ${this.periodInfo
        .periodAssTeachYear || "-"}`;
    },

    isSearchValid() {
      return (
        this.searchForm.idCard.length === 13 &&
        this.searchForm.birthYear.length === 4
      );
    },

    isApproved() {
      return ["confirm", "approve"].includes(this.teacherData.mt_status_report);
    },

    isRejected() {
      return this.teacherData.mt_status_report === "cc";
    },

    hasLicense() {
      return this.teacherData.mt_license_status === "have";
    },

    canConfirm() {
      if (!this.teacherData.mt_picprofile) return false;
      if (!this.teacherData.mt_tel_p) return false;

      if (this.hasLicense) {
        return (
          this.teacherData.pt_licence_file &&
          this.teacherData.pt_licence &&
          this.teacherData.pt_ondate &&
          this.teacherData.pt_enddate
        );
      } else {
        return (
          this.teacherData.mt_ed_file &&
          this.teacherData.mt_transcript &&
          this.teacherData.mt_idcard_copy
        );
      }
    },

    fullName() {
      return `${this.teacherData.mt_title_s}${this.teacherData.mt_frist_name} ${this.teacherData.mt_last_name}`;
    },

    birthDate() {
      return `${this.teacherData.mt_brith_day}-${this.teacherData.mt_brith_month}-${this.teacherData.mt_brith_year}`;
    },

    timestamp() {
      return Math.floor(Date.now() / 1000);
    }
  },

  async mounted() {
    await this.loadPeriodInfo();
    if (this.periodInfo.periodAssTeachEnable !== "1") {
      this.showSystemClosed();
    }
  },

  methods: {
    // ===== Initialization =====
    async loadPeriodInfo() {
      try {
        const { data } = await this.$http.post("periodassteach.php", {
          ApiKey: this.apiKey,
          periodAssTeachID: "103"
        });
        this.periodInfo = data;
      } catch (error) {
        this.showError("ไม่สามารถโหลดข้อมูลระยะเวลาได้");
      }
    },

    showSystemClosed() {
      swal
        .fire({
          icon: "error",
          title: "ปิดระบบ",
          text: "ระบบรายงานตัวปิดให้บริการชั่วคราว",
          allowOutsideClick: false,
          allowEscapeKey: false
        })
        .then(() => {
          this.$router.push("/");
        });
    },

    // ===== Search =====
    formatIdCard() {
      this.searchForm.idCard = this.searchForm.idCard.replace(/\D/g, "");
    },

    async handleSearch() {
      if (!this.isSearchValid) {
        this.showWarning("กรุณากรอกข้อมูลให้ครบถ้วน");
        return;
      }

      this.loading = true;
      try {
        const { data } = await this.$http.post(
          "manage_assistant_teacher_fastq.php",
          {
            ApiKey: this.apiKey,
            mt_id_card: this.searchForm.idCard,
            mt_brith_year: this.searchForm.birthYear,
            mt_times: this.periodInfo.periodAssTeachTimes,
            mt_years: this.periodInfo.periodAssTeachYear
          }
        );

        if (data && data.mt_id_card) {
          this.teacherData = data;
          this.showSuccess("พบข้อมูลของท่าน");
        } else {
          this.showWarning("ไม่พบข้อมูล กรุณาตรวจสอบข้อมูลอีกครั้ง");
          this.teacherData = { mt_id_card: "" };
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการค้นหาข้อมูล");
      } finally {
        this.loading = false;
      }
    },

    // ===== File Upload =====
    async uploadFile(file, folder, prefix) {
      const formData = new FormData();
      const extension = file.name.split(".").pop();
      const filename = `${this.teacherData.mt_id_card}.${this.timestamp}.${prefix}.${extension}`;

      formData.append("ApiKey", this.apiKey);
      formData.append("file", file);
      formData.append("filename", `../${folder}/${filename}`);

      try {
        await this.$http.post("uploadfile.php", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        return filename;
      } catch (error) {
        throw new Error("อัพโหลดไฟล์ไม่สำเร็จ");
      }
    },

    async deleteFile(folder, filename) {
      try {
        await this.$http.post("deletefile.php", {
          ApiKey: this.apiKey,
          filename: `../${folder}/${filename}`
        });
      } catch (error) {
        throw new Error("ลบไฟล์ไม่สำเร็จ");
      }
    },

    // ===== Photo Handlers =====
    async handlePhotoUpload() {
      if (!this.uploadFiles.photo) {
        this.showWarning("กรุณาเลือกไฟล์รูปภาพ");
        return;
      }

      // Validate file before upload
      const file = this.uploadFiles.photo;

      // Check file type
      if (!["image/jpeg", "image/jpg"].includes(file.type)) {
        this.showError("กรุณาเลือกไฟล์ JPG หรือ JPEG เท่านั้น");
        return;
      }

      // Check file size (5 MB)
      if (file.size > 5 * 1024 * 1024) {
        this.showError("ขนาดไฟล์ต้องไม่เกิน 5 MB");
        return;
      }

      this.uploading.photo = true;

      try {
        // Create filename
        const extension = file.name
          .split(".")
          .pop()
          .toLowerCase();
        const filename = `${this.teacherData.mt_id_card}.${this.timestamp}.profile.${extension}`;

        // Create FormData
        const formData = new FormData();
        formData.append("ApiKey", this.apiKey);
        formData.append("file", file);
        formData.append("filename", `../HRvecfiles/${filename}`);

        // Upload file
        const response = await this.$http.post("uploadfile.php", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });

        console.log("Upload Response:", response.data); // Debug

        if (!response.data.status) {
          throw new Error(response.data.message || "Upload failed");
        }

        // Update teacher data with JUST the filename (not full path)
        this.teacherData.mt_picprofile = filename;

        // Save to database
        await this.updateProfile();

        // Clear upload file
        this.uploadFiles.photo = null;

        // Show success
        this.showSuccess("อัพโหลดรูปภาพสำเร็จ");

        // Optional: Verify file is accessible
        await this.verifyFileExists(filename);
      } catch (error) {
        console.error("Upload Error:", error);

        if (error.response && error.response.data) {
          console.error("Server Response:", error.response.data);
          this.showError(error.response.data.message || "อัพโหลดไม่สำเร็จ");
        } else {
          this.showError(error.message || "เกิดข้อผิดพลาดในการอัพโหลด");
        }
      } finally {
        this.uploading.photo = false;
      }
    },

    async verifyFileExists(filename) {
      try {
        const testUrl = `https://hrvec.ovec.go.th/HRvecfiles/${filename}`;
        const response = await fetch(testUrl, { method: "HEAD" });

        if (!response.ok) {
          console.warn("File verification failed:", testUrl);
          this.showWarning(
            "ไฟล์อัพโหลดสำเร็จแต่ไม่สามารถเข้าถึงได้ กรุณาติดต่อผู้ดูแลระบบ"
          );
        } else {
          console.log("File verified:", testUrl);
        }
      } catch (error) {
        console.error("File verification error:", error);
      }
    },

    async handlePhotoDelete() {
      const confirmed = await this.confirmDelete("รูปถ่าย");
      if (!confirmed) return;

      try {
        await this.deleteFile("HRvecfiles", this.teacherData.mt_picprofile);
        this.teacherData.mt_picprofile = "";
        await this.updateProfile();
        this.showSuccess("ลบรูปภาพสำเร็จ");
      } catch (error) {
        this.showError("ไม่สามารถลบรูปภาพได้");
      }
    },

    // ===== License Handlers =====
    async handleLicenseFileUpload() {
      if (!this.uploadFiles.license) return;

      this.uploading.license = true;
      try {
        const filename = await this.uploadFile(
          this.uploadFiles.license,
          "HRvecLicenseTeach",
          "license"
        );
        this.teacherData.pt_licence_file = filename;
        await this.updateProfile();
        this.uploadFiles.license = null;
        this.showSuccess("อัพโหลดใบอนุญาตสำเร็จ");
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.uploading.license = false;
      }
    },

    async handleLicenseFileDelete() {
      const confirmed = await this.confirmDelete("ใบอนุญาตประกอบวิชาชีพครู");
      if (!confirmed) return;

      try {
        await this.deleteFile(
          "HRvecLicenseTeach",
          this.teacherData.pt_licence_file
        );
        this.teacherData.pt_licence_file = "";
        await this.updateProfile();
        this.showSuccess("ลบเอกสารสำเร็จ");
      } catch (error) {
        this.showError("ไม่สามารถลบเอกสารได้");
      }
    },

    // ===== Document Handlers =====
    async handleDocumentUpload(type) {
      const config = {
        education: {
          file: this.uploadFiles.education,
          folder: "HREducation",
          prefix: "ed",
          field: "mt_ed_file",
          label: "สำเนาปริญญาบัตร"
        },
        transcript: {
          file: this.uploadFiles.transcript,
          folder: "HREducation",
          prefix: "transcript",
          field: "mt_transcript",
          label: "ใบรายงานผลการเรียน"
        },
        idCard: {
          file: this.uploadFiles.idCard,
          folder: "HRIDcard",
          prefix: "idcard",
          field: "mt_idcard_copy",
          label: "สำเนาบัตรประชาชน"
        },
        nameChange: {
          file: this.uploadFiles.nameChange,
          folder: "HRIDcard",
          prefix: "namechange",
          field: "mt_change_name",
          label: "เอกสารเปลี่ยนชื่อ-สกุล"
        }
      };

      const cfg = config[type];
      if (!cfg || !cfg.file) return;

      this.uploading[type] = true;
      try {
        const filename = await this.uploadFile(
          cfg.file,
          cfg.folder,
          cfg.prefix
        );
        this.teacherData[cfg.field] = filename;
        await this.updateProfile();
        this.uploadFiles[type] = null;
        this.showSuccess(`อัพโหลด${cfg.label}สำเร็จ`);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.uploading[type] = false;
      }
    },

    async handleDocumentDelete(type) {
      const config = {
        education: {
          folder: "HREducation",
          field: "mt_ed_file",
          label: "สำเนาปริญญาบัตร"
        },
        transcript: {
          folder: "HREducation",
          field: "mt_transcript",
          label: "ใบรายงานผลการเรียน"
        },
        idCard: {
          folder: "HRIDcard",
          field: "mt_idcard_copy",
          label: "สำเนาบัตรประชาชน"
        },
        nameChange: {
          folder: "HRIDcard",
          field: "mt_change_name",
          label: "เอกสารเปลี่ยนชื่อ-สกุล"
        }
      };

      const cfg = config[type];
      if (!cfg) return;

      const confirmed = await this.confirmDelete(cfg.label);
      if (!confirmed) return;

      try {
        await this.deleteFile(cfg.folder, this.teacherData[cfg.field]);
        this.teacherData[cfg.field] = "";
        await this.updateProfile();
        this.showSuccess(`ลบ${cfg.label}สำเร็จ`);
      } catch (error) {
        this.showError("ไม่สามารถลบเอกสารได้");
      }
    },

    viewDocument(type) {
      this.pdfDialogs[type] = true;
    },

    // ===== Update Profile =====
    async updateProfile() {
      try {
        const payload = {
          ...this.teacherData,
          ApiKey: this.apiKey
        };

        const { data } = await this.$http.post(
          "manage_assistant_teacher.update.php",
          payload
        );

        if (!data.status) {
          throw new Error("บันทึกข้อมูลไม่สำเร็จ");
        }

        return true;
      } catch (error) {
        throw error;
      }
    },

    // ===== Confirm Submission =====
    async handleConfirm() {
      if (!this.$refs.profileForm.validate()) {
        this.showWarning("กรุณากรอกข้อมูลให้ครบถ้วน");
        return;
      }

      const confirmed = await Swal.fire({
        title: "ยืนยันรายการ",
        html: `
          <p style="font-size: 16px; line-height: 1.8;">หากกดยืนยัน จะไม่สามารถแก้ไขรายการใดๆ ได้อีก</p>
          <p style="color: red; font-weight: bold;">กรุณาตรวจสอบให้ถูกต้องก่อนกดปุ่มยืนยัน</p>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#2E7D32",
        cancelButtonColor: "#f44336",
        confirmButtonText: '<i class="v-icon mdi mdi-check"></i> ยืนยัน',
        cancelButtonText: '<i class="v-icon mdi mdi-close"></i> ยกเลิก'
      });

      if (!confirmed.isConfirmed) return;

      this.submitting = true;
      try {
        this.teacherData.mt_status_report = "confirm";
        await this.updateProfile();

        await Swal.fire({
          icon: "success",
          title: "ยืนยันรายการสำเร็จ",
          text: "ระบบได้บันทึกข้อมูลของท่านเรียบร้อยแล้ว",
          timer: 2000,
          showConfirmButton: false
        });

        await this.handleSearch();
      } catch (error) {
        this.showError("ยืนยันรายการไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      } finally {
        this.submitting = false;
      }
    },

    // ===== Utilities =====
    formatThaiDate(dateString) {
      if (!dateString) return "-";

      const date = new Date(dateString);
      const thaiMonths = [
        "มกราคม",
        "กุมภาพันธ์",
        "มีนาคม",
        "เมษายน",
        "พฤษภาคม",
        "มิถุนายน",
        "กรกฎาคม",
        "สิงหาคม",
        "กันยายน",
        "ตุลาคม",
        "พฤศจิกายน",
        "ธันวาคม"
      ];

      const day = date.getDate();
      const month = thaiMonths[date.getMonth()];
      const year = date.getFullYear() + 543;

      return `${day} ${month} ${year}`;
    },

    async confirmDelete(itemName) {
      const result = await Swal.fire({
        title: `ยืนยันการลบ`,
        html: `<p style="font-size: 16px;">คุณต้องการลบ <strong>${itemName}</strong> หรือไม่?</p>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f44336",
        cancelButtonColor: "#9E9E9E",
        confirmButtonText: '<i class="v-icon mdi mdi-delete"></i> ลบ',
        cancelButtonText: '<i class="v-icon mdi mdi-close"></i> ยกเลิก'
      });
      return result.isConfirmed;
    },

    showSuccess(message) {
      Swal.fire({
        icon: "success",
        title: "สำเร็จ",
        text: message,
        timer: 2000,
        showConfirmButton: false
      });
    },

    showError(message) {
      Swal.fire({
        icon: "error",
        title: "ข้อผิดพลาด",
        text: message,
        confirmButtonText: "ตกลง"
      });
    },

    showWarning(message) {
      Swal.fire({
        icon: "warning",
        title: "คำเตือน",
        text: message,
        confirmButtonText: "ตกลง"
      });
    }
  }
};
</script>

<style scoped>
/* ===== Typography ===== */
.text-h5 {
  font-size: 1.5rem !important;
  font-weight: 500;
  line-height: 2rem;
}

.text-h6 {
  font-size: 1.25rem !important;
  font-weight: 500;
  line-height: 1.75rem;
}

.body-1 {
  font-size: 1rem;
  line-height: 1.75rem;
}

.body-2 {
  font-size: 0.875rem;
  line-height: 1.5rem;
}

/* ===== Cards ===== */
.v-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px !important;
}

.approved-card {
  border: 2px solid #2e7d32;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.15) !important;
}

.edit-card {
  border: 2px solid #455a64;
}

.v-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

/* ===== Profile Avatar ===== */
.profile-avatar {
  border: 4px solid #e0e0e0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* ===== Info Items ===== */
.info-item {
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;
}

.info-item:last-child {
  border-bottom: none;
}

.info-item strong {
  color: #424242;
  font-size: 0.9rem;
}

.info-item p {
  margin: 4px 0 0 0;
  font-size: 1rem;
  color: #212121;
}

/* ===== License Section ===== */
.license-section {
  background: linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%);
  border-radius: 8px;
}

/* ===== Document Items ===== */
.document-item {
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 8px;
}

.document-item:hover {
  background-color: #f5f5f5;
  transform: translateX(4px);
}

/* ===== Upload Section ===== */
.upload-section {
  padding: 20px;
  background-color: #fafafa;
  border-radius: 8px;
  border: 2px dashed #e0e0e0;
}

.document-upload-section {
  padding: 16px;
  background-color: #fafafa;
  border-radius: 8px;
  margin-bottom: 12px;
}

/* ===== Alerts ===== */
.v-alert {
  border-radius: 8px !important;
}

/* ===== Buttons ===== */
.v-btn {
  text-transform: none !important;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.v-btn--x-large {
  min-width: 250px;
  padding: 16px 32px !important;
  font-size: 1.1rem !important;
}

.v-btn--large {
  min-height: 48px !important;
}

/* ===== Input Fields ===== */
.v-text-field--outlined >>> fieldset {
  border-radius: 8px;
}

.v-text-field >>> .v-input__control {
  border-radius: 8px;
}

.v-file-input >>> .v-input__control {
  border-radius: 8px;
}

/* ===== Divider ===== */
.v-divider {
  border-color: rgba(0, 0, 0, 0.08);
}

/* ===== Dialog ===== */
.v-dialog > .v-card {
  border-radius: 12px;
  overflow: hidden;
}

/* ===== Chips ===== */
.v-chip {
  font-weight: 500;
  padding: 0 16px;
}

/* ===== Lists ===== */
.v-list-item {
  transition: all 0.2s;
}

.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

/* ===== Animations ===== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.v-card {
  animation: fadeIn 0.4s ease-out;
}

/* ===== Status Colors ===== */
.status-approved {
  color: #2e7d32 !important;
}

.status-pending {
  color: #a86400 !important;
}

.status-rejected {
  color: #f44336 !important;
}

/* ===== Loading Overlay ===== */
.v-overlay {
  backdrop-filter: blur(3px);
}

/* ===== Scrollbar Styling ===== */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* ===== Responsive ===== */
@media (max-width: 960px) {
  .text-h5 {
    font-size: 1.25rem !important;
  }

  .text-h6 {
    font-size: 1.1rem !important;
  }

  .v-btn--x-large {
    min-width: 200px;
    font-size: 1rem !important;
  }

  .profile-avatar {
    width: 150px !important;
    height: 150px !important;
  }

  .info-item {
    padding: 8px 0;
  }
}

@media (max-width: 600px) {
  .profile-avatar {
    width: 120px !important;
    height: 120px !important;
  }

  .v-btn--x-large {
    min-width: 100%;
  }

  .license-section {
    padding: 12px !important;
  }

  .document-upload-section {
    padding: 12px;
  }
}

/* ===== Print Styles ===== */
@media print {
  .v-btn,
  .v-file-input,
  .v-alert {
    display: none !important;
  }

  .v-card {
    box-shadow: none !important;
    border: 1px solid #e0e0e0;
  }
}

/* ===== Dark Mode Support ===== */
.theme--dark .info-item {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .license-section {
  background: linear-gradient(135deg, #2a2a2a 0%, #1e1e1e 100%);
}

.theme--dark .upload-section,
.theme--dark .document-upload-section {
  background-color: #2a2a2a;
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .document-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

/* ===== Focus Styles ===== */
.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-width: 2px;
}

.v-btn:focus {
  outline: 2px solid rgba(33, 150, 243, 0.3);
  outline-offset: 2px;
}

/* ===== Transition Effects ===== */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.v-enter,
.v-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
